import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  acceptAllConsent,
  getConsentSnapshot,
  rejectNonEssentialConsent,
  subscribeToConsentChanges,
  type ConsentSnapshot,
} from '@services/consent';

function hasAcceptedAll(snapshot: ConsentSnapshot): boolean {
  return (
    snapshot.hasDecision &&
    snapshot.preferences.analytics &&
    snapshot.preferences.adsMeasurement
  );
}

const labelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.75rem',
  fontSize: '0.9rem',
  lineHeight: 1.4,
};

const checkboxStyle: CSSProperties = {
  width: '1.25rem',
  height: '1.25rem',
  margin: 0,
  flexShrink: 0,
  cursor: 'pointer',
  accentColor: 'var(--mantine-color-blue-6)',
};

/**
 * Offers optional consent alongside the anonymous upload agreement without making it part of
 * upload eligibility. Selecting it keeps it visibly checked until the Terms choice closes the
 * agreement area; a later upload omits it when all optional choices are already granted.
 */
export function UploadPrivacyConsentChoice() {
  const { t } = useTranslation();
  const [initialSnapshot] = useState(() => getConsentSnapshot());
  const [showForCurrentUpload, setShowForCurrentUpload] = useState(
    () => !hasAcceptedAll(initialSnapshot),
  );
  const [acceptedAll, setAcceptedAll] = useState(() => hasAcceptedAll(initialSnapshot));

  useEffect(
    () => subscribeToConsentChanges((snapshot) => {
      const nextAcceptedAll = hasAcceptedAll(snapshot);
      setAcceptedAll(nextAcceptedAll);
      if (!nextAcceptedAll) {
        setShowForCurrentUpload(true);
      }
    }),
    [],
  );

  if (!showForCurrentUpload) {
    return null;
  }

  return (
    <label style={labelStyle}>
      <input
        type="checkbox"
        checked={acceptedAll}
        onChange={(event) => {
          const snapshot = event.target.checked
            ? acceptAllConsent()
            : rejectNonEssentialConsent();
          setAcceptedAll(hasAcceptedAll(snapshot));
        }}
        style={checkboxStyle}
      />
      <span>
        {t('upload.acceptAllPrivacyChoices', 'I accept all privacy choices')}
      </span>
    </label>
  );
}
